import React, { useState, useEffect, useMemo } from 'react';
import { useUI } from '../../../components/common/UIMessageProvider';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  AttendanceRecord,
  getAttendanceRecords,
  deleteAttendanceRecord,
  updateAttendanceRecord,
  createAttendanceRecord,
  getLibraryCategories,
  LibraryCategory
} from '../../../firebase/libraryFirestoreService';

import AttendanceForm from './AttendanceForm';
import AttendanceStats from './AttendanceStats';
import AttendanceFilters from './AttendanceFilters';
import AttendanceTable from './AttendanceTable';
import AttendancePagination from './AttendancePagination';

// Cache key and expiry
const ATTENDANCE_CACHE_KEY = 'attendance_records_cache';
const ATTENDANCE_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
const CATEGORIES_CACHE_KEY = 'attendance_categories_cache';
const CATEGORIES_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

const AttendanceManagement: React.FC = () => {
  const ui = useUI();
  const auth = useAuth();
  
  const canViewAttendance = auth.canPerformAction('library', 'view') || auth.canPerformAction('attendance', 'view');
  const canAddAttendance = auth.canPerformAction('library', 'create') || auth.canPerformAction('attendance', 'create');
  const canEditAttendance = auth.canPerformAction('library', 'edit') || auth.canPerformAction('attendance', 'edit');
  const canDeleteAttendance = auth.canPerformAction('library', 'delete') || auth.canPerformAction('attendance', 'delete');
  const canExportAttendance = auth.canPerformAction('library', 'export') || auth.canPerformAction('attendance', 'export');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Attendance data state
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [showAttendanceForm, setShowAttendanceForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<AttendanceRecord | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Categories state
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  
  // Date filters
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'custom' | 'all'>('today');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Statistics
  const [stats, setStats] = useState({
    totalRecords: 0,
    uniqueMembers: 0,
    booksRead: 0,
    dailyAverage: 0,
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Initialize
  useEffect(() => {
    if (!canViewAttendance) return;
    
    setCurrentPage(1);
    fetchCategories();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFiltersToCachedData();
  }, [dateFilter, startDate, endDate, debouncedSearchTerm, allAttendanceRecords]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(attendanceRecords.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [attendanceRecords.length]);

  // Cache functions
  const isCacheValid = (cacheKey: string, expiry: number): boolean => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return false;
      const { timestamp } = JSON.parse(cacheData);
      return Date.now() - timestamp < expiry;
    } catch (error) {
      console.warn('Cache read error:', error);
      return false;
    }
  };

  const getCachedData = <T,>(cacheKey: string): T[] => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return [];
      const { data } = JSON.parse(cacheData);
      return data;
    } catch (error) {
      console.warn('Cache parse error:', error);
      return [];
    }
  };

  const saveToCache = async <T,>(cacheKey: string, data: T[]): Promise<boolean> => {
    try {
      const cacheData = {
        timestamp: Date.now(),
        data,
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('saveToCache error:', error);
      return false;
    }
  };

  const invalidateCache = (cacheKey: string) => {
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Failed to invalidate cache:', error);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    if (isCacheValid(CATEGORIES_CACHE_KEY, CATEGORIES_CACHE_EXPIRY)) {
      const cachedCategories = getCachedData<LibraryCategory>(CATEGORIES_CACHE_KEY);
      setCategories(cachedCategories);
      
      const mapping: Record<string, string> = {};
      cachedCategories.forEach(category => {
        const cid = category.id?.toString() || '';
        if (cid) mapping[cid] = category.name;
      });
      setCategoryMap(mapping);
      return;
    }

    try {
      const result = await getLibraryCategories();
      if (result.success) {
        setCategories(result.data);
        
        const mapping: Record<string, string> = {};
        result.data.forEach(category => {
          const cid = category.id?.toString() || '';
          if (cid) mapping[cid] = category.name;
        });
        setCategoryMap(mapping);
        
        await saveToCache(CATEGORIES_CACHE_KEY, result.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    return categoryMap[String(categoryId)] || `Category ${categoryId}`;
  };

  // Date helpers
  const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getDateRange = () => {
    const now = new Date();
    let start: Date, end: Date;
    
    switch(dateFilter) {
      case 'today':
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        start = new Date(now);
        start.setDate(now.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start = new Date(now);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (startDate && endDate) {
          start = parseLocalDate(startDate);
          start.setHours(0, 0, 0, 0);
          end = parseLocalDate(endDate);
          end.setHours(23, 59, 59, 999);
        } else {
          start = new Date();
          start.setHours(0, 0, 0, 0);
          end = new Date();
          end.setHours(23, 59, 59, 999);
        }
        break;
      default: // 'all'
        return { start: null, end: null };
    }
    
    return { start, end };
  };

  const applyFiltersToCachedData = () => {
    if (allAttendanceRecords.length === 0) return;

    let filteredRecords = [...allAttendanceRecords];

    // Apply date filter
    if (dateFilter !== 'all') {
      const { start, end } = getDateRange();
      if (start && end) {
        filteredRecords = filteredRecords.filter(record => {
          const recordDate = new Date(record.timeRecorded);
          return recordDate >= start && recordDate <= end;
        });
      }
    }

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filteredRecords = filteredRecords.filter(record => {
        const categoryName = getCategoryName(record.categoryId).toLowerCase();
        
        return (
          record.memberId?.toLowerCase().includes(searchLower) ||
          record.fullName?.toLowerCase().includes(searchLower) ||
          record.bookTitle?.toLowerCase().includes(searchLower) ||
          categoryName.includes(searchLower)
        );
      });
    }

    // Sort by time recorded (newest first)
    filteredRecords.sort((a, b) => {
      return new Date(b.timeRecorded).getTime() - new Date(a.timeRecorded).getTime();
    });

    setAttendanceRecords(filteredRecords);
    setCurrentPage(1);
    
    // Calculate statistics from filtered data
    calculateStatistics(filteredRecords);
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const scopedCacheKey = start && end
        ? `${ATTENDANCE_CACHE_KEY}_${dateFilter}_${getLocalDateString(start)}_${getLocalDateString(end)}`
        : `${ATTENDANCE_CACHE_KEY}_recent`;

      // Try to use cache first
      if (isCacheValid(scopedCacheKey, ATTENDANCE_CACHE_EXPIRY)) {
        const cachedRecords = getCachedData<AttendanceRecord>(scopedCacheKey);
        setAllAttendanceRecords(cachedRecords);
        setAttendanceRecords(cachedRecords);
        console.log('📚 Using cached attendance records');
        setLoading(false);
        calculateStatistics(cachedRecords);
        return;
      }

      // If no valid cache, fetch from Firebase
      const result = await getAttendanceRecords(
        start && end
          ? { startDate: start, endDate: end, limitCount: 500 }
          : { limitCount: 500 }
      );
      
      if (result.success) {
        // Save to cache
        await saveToCache(scopedCacheKey, result.data);
        
        // Update state
        setAllAttendanceRecords(result.data);
        setAttendanceRecords(result.data);
        
        console.log('🔄 Fetched attendance from Firebase and cached');
        calculateStatistics(result.data);
      } else {
        console.error('Error fetching attendance:', result.error);
        // Try to use stale cache if available
        const staleCache = getCachedData<AttendanceRecord>(scopedCacheKey);
        if (staleCache.length > 0) {
          setAllAttendanceRecords(staleCache);
          setAttendanceRecords(staleCache);
          calculateStatistics(staleCache);
          ui.showMessage('Using cached data (offline mode)', { type: 'info' });
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      // Try to use stale cache if available
      const staleCache = getCachedData<AttendanceRecord>(`${ATTENDANCE_CACHE_KEY}_recent`);
      if (staleCache.length > 0) {
        setAllAttendanceRecords(staleCache);
        setAttendanceRecords(staleCache);
        calculateStatistics(staleCache);
        ui.showMessage('Using cached data (offline mode)',{ type: 'info' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewAttendance) return;
    fetchAttendance();
  }, [canViewAttendance, dateFilter, startDate, endDate]);

  const calculateStatistics = (records: AttendanceRecord[]) => {
    const uniqueMembers = new Set(records.map(record => record.memberId));
    const uniqueBooks = new Set(records.map(record => record.bookTitle));
    
    let dailyAverage = 0;
    if (records.length > 0 && dateFilter !== 'all') {
      const { start, end } = getDateRange();
      if (start && end) {
        const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
        const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
        const daysDiff = Math.floor((endUTC - startUTC) / (1000 * 60 * 60 * 24)) + 1;
        dailyAverage = daysDiff > 0 ? records.length / daysDiff : 0;
      }
    }

    setStats({
      totalRecords: records.length,
      uniqueMembers: uniqueMembers.size,
      booksRead: uniqueBooks.size,
      dailyAverage: Math.round(dailyAverage * 100) / 100,
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setDebouncedSearchTerm(searchTerm);
    applyFiltersToCachedData();
  };

  const handleSaveAttendance = async (formData: any) => {
    setFormLoading(true);
    try {
      if (editingRecord && editingRecord.id) {
        // Update existing record
        const result = await updateAttendanceRecord(editingRecord.id, {
          ...formData,
          categoryId: String(formData.categoryId),
          timeRecorded: new Date(formData.timeRecorded),
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to update record');
        }
        ui.showMessage('Attendance updated successfully!');
      } else {
        // Prevent duplicate attendance for same member on the same date
        const newDate = new Date(formData.timeRecorded);
        const newDateLocalStr = getLocalDateString(newDate);
        const memberIdNormalized = String(formData.memberId || '').trim().toUpperCase();

        const duplicate = allAttendanceRecords.find(r => {
          const rMember = String(r.memberId || '').trim().toUpperCase();
          const rDate = new Date(r.timeRecorded);
          const rLocalDate = getLocalDateString(rDate);
          return rMember === memberIdNormalized && rLocalDate === newDateLocalStr;
        });

        if (duplicate) {
          ui.showMessage('This member already has an attendance record for the selected date.', { type: 'warning' });
          setFormLoading(false);
          return;
        }

        // Create new record
        const result = await createAttendanceRecord({
          memberId: formData.memberId,
          fullName: formData.fullName,
          bookTitle: formData.bookTitle,
          categoryId: String(formData.categoryId),
          timeRecorded: new Date(formData.timeRecorded),
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to create record');
        }
        ui.showMessage('Attendance saved successfully!');
      }
      
      // Invalidate cache and refetch
      invalidateCache(ATTENDANCE_CACHE_KEY);
      await fetchAttendance();
      setShowAttendanceForm(false);
      setEditingRecord(null);
    } catch (error: any) {
      console.error('Save error:', error);
      ui.showMessage(`Error saving attendance: ${error.message}`, { type: 'error' });
      throw error; // Re-throw for form to handle
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRecord = async (recordId: string) => {
    const ok = await ui.askConfirm({ 
      title: 'Delete Attendance', 
      message: 'Are you sure you want to delete this attendance record?' 
    });
    if (!ok) return;

    try {
      setLoading(true);
      const result = await deleteAttendanceRecord(recordId);
      if (result.success) {
        // Invalidate cache and refetch
        invalidateCache(ATTENDANCE_CACHE_KEY);
        await fetchAttendance();
        ui.showMessage('Attendance record deleted successfully!');
      } else {
        throw new Error(result.error || 'Failed to delete record');
      }
    } catch (error) {
      console.error('Error deleting attendance record:', error);
      const msg = error instanceof Error ? error.message : String(error);
      ui.showMessage(`Error deleting attendance record: ${msg}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttendance = () => {
    setEditingRecord(null);
    setShowAttendanceForm(true);
  };

  const handleEditClick = (record: AttendanceRecord) => {
    setEditingRecord(record);
    setShowAttendanceForm(true);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const dateRangeText = dateFilter === 'today' ? 'Today' :
                          dateFilter === 'week' ? 'Last 7 Days' :
                          dateFilter === 'month' ? 'This Month' :
                          dateFilter === 'custom' ? `${startDate} to ${endDate}` :
                          'All Time';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Library Attendance Report</title>
        <style>
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box; 
          }
          
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px; 
            background: linear-gradient(135deg, #0f766e 0%, #0e7490 100%);
            min-height: 100vh;
          }
          
          .container {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            overflow: hidden;
          }
          
          .header { 
            background: linear-gradient(135deg, #0f766e 0%, #0e7490 100%);
            color: white;
            padding: 30px;
            text-align: center;
            position: relative;
            overflow: hidden;
          }
          
          .header::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
            animation: pulse 4s ease-in-out infinite;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
          
          .header-content {
            position: relative;
            z-index: 1;
          }

          .foundation-name {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 12px;
            letter-spacing: 1px;
            opacity: 0.95;
          }
          
          .header h1 { 
            font-size: 32px;
            font-weight: 700;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 2px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
          }
          
          .header p { 
            font-size: 14px;
            opacity: 0.95;
            font-weight: 300;
          }
          
          .stats-section {
            padding: 25px;
            background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
          }
          
          .stats-grid { 
            display: grid; 
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .stat-card { 
            background: white;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border-left: 4px solid;
            transition: transform 0.2s;
            position: relative;
            overflow: hidden;
          }
          
          .stat-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, transparent, currentColor, transparent);
            opacity: 0.3;
          }
          
          .stat-card.teal { border-left-color: #0f766e; color: #0f766e; }
          .stat-card.cyan { border-left-color: #0891b2; color: #0891b2; }
          .stat-card.blue { border-left-color: #0369a1; color: #0369a1; }
          .stat-card.emerald { border-left-color: #059669; color: #059669; }
          
          .stat-label {
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            opacity: 0.8;
            margin-bottom: 10px;
          }
          
          .stat-value {
            font-size: 32px;
            font-weight: 700;
            line-height: 1;
          }
          
          .table-container {
            padding: 0 25px 25px;
          }
          
          table { 
            width: 100%; 
            border-collapse: separate;
            border-spacing: 0;
            font-size: 11px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border-radius: 8px;
            overflow: hidden;
          }
          
          thead {
            background: linear-gradient(135deg, #0f766e 0%, #0e7490 100%);
            color: white;
          }
          
          th { 
            padding: 12px 10px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
            border-bottom: 2px solid rgba(255,255,255,0.2);
          }
          
          tbody tr {
            background: white;
            transition: background 0.2s;
          }
          
          tbody tr:nth-child(even) {
            background: #f9fafb;
          }
          
          tbody tr:hover {
            background: #f3f4f6;
          }
          
          td { 
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
          }
          
          tbody tr:last-child td {
            border-bottom: none;
          }
          
          .footer {
            background: linear-gradient(to right, #f9fafb, #f3f4f6);
            padding: 15px 25px;
            border-top: 2px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            color: #6b7280;
            flex-wrap: wrap;
            gap: 10px;
          }
          
          .footer-left {
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
          }

          .footer-right {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 3px;
          }

          .copyright {
            font-size: 10px;
            color: #9ca3af;
            font-style: italic;
          }
          
          .footer-item {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          
          .footer-label {
            font-weight: 600;
            color: #374151;
          }
          
          @media print {
            body { 
              padding: 0;
              background: white;
            }
            
            .container {
              box-shadow: none;
            }
            
            .stat-card {
              break-inside: avoid;
            }
            
            tbody tr {
              break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="header-content">
              <div class="foundation-name">ALHAJI INUSAH FOUNDATION</div>
              <h1>LIBRARY ATTENDANCE REPORT</h1>
              <p>Period: ${dateRangeText} | Generated on ${new Date().toLocaleString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>
          </div>
          
          <div class="stats-section">
            <div class="stats-grid">
              <div class="stat-card teal">
                <div class="stat-label">Total Records</div>
                <div class="stat-value">${stats.totalRecords}</div>
              </div>
              <div class="stat-card cyan">
                <div class="stat-label">Unique Members</div>
                <div class="stat-value">${stats.uniqueMembers}</div>
              </div>
              <div class="stat-card blue">
                <div class="stat-label">Books Read</div>
                <div class="stat-value">${stats.booksRead}</div>
              </div>
              <div class="stat-card emerald">
                <div class="stat-label">Daily Average</div>
                <div class="stat-value">${stats.dailyAverage.toFixed(1)}</div>
              </div>
            </div>
          </div>
          
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Member ID</th>
                  <th>Full Name</th>
                  <th>Book Title</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                ${attendanceRecords.map((record, i) => `
                  <tr>
                    <td><strong>${i + 1}</strong></td>
                    <td style="font-family: monospace; font-weight: 500;">${record.memberId}</td>
                    <td style="font-weight: 600; color: #111827;">${record.fullName}</td>
                    <td>${record.bookTitle}</td>
                    <td>${getCategoryName(record.categoryId)}</td>
                    <td>${getLocalDateString(new Date(record.timeRecorded))}</td>
                    <td>${new Date(record.timeRecorded).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            <div class="footer-left">
              <div class="footer-item">
                <span class="footer-label">Total Records:</span>
                <span>${attendanceRecords.length}</span>
              </div>
              <div class="footer-item">
                <span class="footer-label">Period:</span>
                <span>${dateRangeText}</span>
              </div>
            </div>
            <div class="footer-right">
              <div>
                <span class="footer-label">Report Date:</span> ${new Date().toLocaleDateString()}
              </div>
              <div class="copyright">
                © ${new Date().getFullYear()} Alhaji Inusah Foundation. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleExportCSV = () => {
    const escapeCSVField = (field: any): string => {
      if (field === null || field === undefined || field === '') return '';
      const str = String(field);
      if (str.includes(',') || str.includes('\n') || str.includes('"') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['Member ID', 'Full Name', 'Book Title', 'Category', 'Date', 'Time'];
    const csvContent = [
      headers.map(escapeCSVField).join(','),
      ...attendanceRecords.map(record => [
        record.memberId,
        record.fullName,
        record.bookTitle,
        getCategoryName(record.categoryId),
        getLocalDateString(new Date(record.timeRecorded)),
        new Date(record.timeRecorded).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      ].map(escapeCSVField).join(','))
    ].join('\r\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${dateFilter}_${getLocalDateString(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    invalidateCache(ATTENDANCE_CACHE_KEY);
    fetchAttendance();
  };

  const formatDate = (date: Date | string) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Cache status indicator
  const cacheStatus = useMemo(() => {
    if (loading) return 'Loading...';
    if (isCacheValid(ATTENDANCE_CACHE_KEY, ATTENDANCE_CACHE_EXPIRY)) return '📚 Using cached data';
    return '🔄 Online data';
  }, [loading, allAttendanceRecords.length]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(attendanceRecords.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedRecords = attendanceRecords.slice(startIndex, endIndex);

  if (!canViewAttendance) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-gray-500">You don't have permission to view attendance records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Attendance Form - At the top */}
      {showAttendanceForm && canAddAttendance && (
        <AttendanceForm
          editingRecord={editingRecord ? {
            id: editingRecord.id,
            memberId: editingRecord.memberId,
            fullName: editingRecord.fullName,
            bookTitle: editingRecord.bookTitle,
            categoryId: editingRecord.categoryId,
            timeRecorded: new Date(editingRecord.timeRecorded).toISOString().slice(0, 16),
          } : undefined}
          onCancel={() => {
            setShowAttendanceForm(false);
            setEditingRecord(null);
          }}
          onSave={handleSaveAttendance}
          loading={formLoading}
        />
      )}

      {/* Statistics Cards */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Statistics */}
          <AttendanceStats stats={stats} />

          {/* Filters and Controls */}
          <div className="flex items-center gap-2">
            <AttendanceFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearchSubmit={handleSearch}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              startDate={startDate}
              onStartDateChange={setStartDate}
              endDate={endDate}
              onEndDateChange={setEndDate}
              onExport={handleExportCSV}
              onRefresh={handleRefresh}
              onAddClick={handleAddAttendance}
              onPrint={handlePrint}
              cacheStatus={cacheStatus}
              canExport={canExportAttendance}
              canAdd={canAddAttendance}
              canPrint={canExportAttendance}
              hasRecords={attendanceRecords.length > 0}
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading attendance records...</p>
            </div>
          </div>
        ) : (
          <>
            <AttendanceTable
              records={displayedRecords}
              getCategoryName={getCategoryName}
              formatDate={formatDate}
              onEdit={handleEditClick}
              onDelete={handleDeleteRecord}
              canEdit={canEditAttendance}
              canDelete={canDeleteAttendance}
            />

            {/* Pagination */}
            <AttendancePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              startIndex={startIndex}
              endIndex={endIndex}
              totalRecords={attendanceRecords.length}
              allRecordsCount={allAttendanceRecords.length}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceManagement;
